import * as React from 'react'
import {useSelector} from 'react-redux'

import {FieldValidation, ValidationScope} from 'airship/components/Validator'
import TextInput from 'airship/components/Validator/TextInput'
import {
  isRequired,
  isValidUrl,
  length,
} from 'airship/components/Validator/validators'
import Box from 'airship/patterns/Box'
import Button from 'airship/patterns/button'
import Label from 'airship/patterns/label'

import detailsSlice from './detailsSlice'
import {
  selectAppKey,
  selectAppName,
  selectProjectId,
  selectProjectName,
  selectUrl,
} from './selectors'
import {useInProductSupportDispatch} from './store'

export interface EditDetailsProps {
  setShowEditDetails: (val: boolean) => void
}

const EditDetails: React.FC<EditDetailsProps> = ({setShowEditDetails}) => {
  const dispatch = useInProductSupportDispatch()

  const appName = useSelector(selectAppName)
  const url = useSelector(selectUrl)
  const appKey = useSelector(selectAppKey)
  const walletProjectId = useSelector(selectProjectId)
  const walletProjectName = useSelector(selectProjectName)

  const isWallet = typeof walletProjectId !== 'undefined'

  const [isValid, setIsValid] = React.useState<boolean>()

  //State for Applications
  const [appN, setAppN] = React.useState(appName)
  const [appK, setAppK] = React.useState(appKey)

  //State for Wallet projects
  const [projectName, setProjectName] = React.useState(walletProjectName)
  const [projectId, setProjectId] = React.useState(walletProjectId)

  const [webAdd, setWebAdd] = React.useState<string>(url)

  const handleUpdate = (): void => {
    if (isWallet) {
      dispatch(
        detailsSlice.actions.updateDetails({
          url: webAdd.trim(),
          projectName: projectName,
          projectId: projectId,
        })
      )
    } else {
      dispatch(
        detailsSlice.actions.updateDetails({
          url: webAdd.trim(),
          appName: appN,
          appKey: appK,
        })
      )
    }

    setShowEditDetails(false)
    dispatch(detailsSlice.actions.clearErrorMessage())
  }

  return (
    <>
      <Box displayClasses="u-bg-grey-40 u-border-none u-border-radius-md u-pad-top-05">
        <ValidationScope onValid={setIsValid}>
          <FieldValidation
            id="projectName"
            value={isWallet ? projectName : appN}
            validators={[length({max: 255, null: true})]}
            asyncValidators={[]}
          >
            <TextInput
              id="projectName"
              label="Project name"
              displayClasses="u-bg-white u-space-bottom-05"
              value={isWallet ? projectName : appN}
              onChange={isWallet ? setProjectName : setAppN}
            />
          </FieldValidation>
          <FieldValidation
            id="url"
            value={webAdd}
            validators={[isRequired(), isValidUrl()]}
            asyncValidators={[]}
          >
            <TextInput
              label="Page URL"
              displayClasses="u-bg-white u-space-bottom-05"
              id="url"
              value={webAdd}
              onChange={setWebAdd}
            />
          </FieldValidation>
          <div className="u-flex">
            <p className="u-text-bold u-text-shrink-1 u-space-right-1">
              {isWallet ? 'Project Wallet ID' : 'App key'}
            </p>
            <a
              href="https://docs.airship.com/guides/messaging/user-guide/admin/security/app-keys-secrets/"
              target="_blank"
              rel="noopener noreferrer"
            >
              What is this?
            </a>
          </div>
          <FieldValidation
            id="app-key"
            value={isWallet ? projectId : appK}
            validators={[length({max: 255, null: true})]}
            asyncValidators={[]}
          >
            <Label hideLabel text="App Key">
              <TextInput
                displayClasses="u-bg-white u-space-bottom-05"
                id="app-key"
                value={isWallet ? projectId : appK}
                onChange={isWallet ? setProjectId : setAppK}
              />
            </Label>
          </FieldValidation>
        </ValidationScope>
        <div className="u-flex">
          <Button
            onClick={handleUpdate}
            style="navy"
            displayClasses="u-space-right-05"
            disabled={!isValid}
          >
            Update details
          </Button>
          <Button
            displayClasses="u-bg-grey-50"
            onClick={() => setShowEditDetails(false)}
          >
            Cancel
          </Button>
        </div>
      </Box>
    </>
  )
}

export default EditDetails
